export interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  body?: unknown;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount: number;
  maxRetries?: number;
}

interface RetryQueueConfig {
  maxRetries?: number;
  retryDelay?: number;
  maxQueueSize?: number;
  storageKey?: string;
}

const DEFAULT_CONFIG: RetryQueueConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  maxQueueSize: 100,
  storageKey: "hrshakti_retry_queue",
};

class RetryQueue {
  private queue: QueuedRequest[] = [];
  private config: RetryQueueConfig;
  private isProcessing = false;
  private listeners: Set<(queue: QueuedRequest[]) => void> = new Set();

  constructor(config?: RetryQueueConfig) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(this.config.storageKey!);
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch {
      this.queue = [];
    }
  }

  private saveToStorage(): void {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(this.config.storageKey!, JSON.stringify(this.queue));
    } catch {
      // Storage full or unavailable
    }
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.queue);
    }
  }

  enqueue(request: Omit<QueuedRequest, "id" | "timestamp" | "retryCount">): string {
    if (this.queue.length >= (this.config.maxQueueSize ?? 100)) {
      this.queue.shift();
    }

    const queuedRequest: QueuedRequest = {
      ...request,
      id: `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: request.maxRetries ?? this.config.maxRetries ?? 3,
    };

    this.queue.push(queuedRequest);
    this.saveToStorage();
    this.notifyListeners();

    if (navigator.onLine) {
      this.processQueue();
    }

    return queuedRequest.id;
  }

  dequeue(id: string): boolean {
    const index = this.queue.findIndex((r) => r.id === id);
    if (index !== -1) {
      this.queue.splice(index, 1);
      this.saveToStorage();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  clear(): void {
    this.queue = [];
    this.saveToStorage();
    this.notifyListeners();
  }

  getQueue(): QueuedRequest[] {
    return [...this.queue];
  }

  getPendingCount(): number {
    return this.queue.length;
  }

  subscribe(listener: (queue: QueuedRequest[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.queue.length > 0) {
      const request = this.queue[0];
      if (!request) break;

      try {
        await this.executeRequest(request);
        this.queue.shift();
        this.saveToStorage();
        this.notifyListeners();
      } catch {
        request.retryCount++;
        if (request.retryCount >= (request.maxRetries ?? 3)) {
          this.queue.shift();
          this.saveToStorage();
          this.notifyListeners();
        } else {
          break;
        }
      }
    }

    this.isProcessing = false;
  }

  private async executeRequest(request: QueuedRequest): Promise<void> {
    const response = await fetch(request.url, {
      method: request.method,
      headers: {
        "Content-Type": "application/json",
        ...request.headers,
      },
      body: request.body ? JSON.stringify(request.body) : undefined,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
  }

  async retryFailed(): Promise<void> {
    if (this.queue.length === 0) return;
    await this.processQueue();
  }
}

let retryQueueInstance: RetryQueue | null = null;

export function getRetryQueue(config?: RetryQueueConfig): RetryQueue {
  if (!retryQueueInstance) {
    retryQueueInstance = new RetryQueue(config);
  }
  return retryQueueInstance;
}

export function enqueueRequest(
  request: Omit<QueuedRequest, "id" | "timestamp" | "retryCount">
): string {
  const queue = getRetryQueue();
  return queue.enqueue(request);
}

export function getPendingRequestCount(): number {
  const queue = getRetryQueue();
  return queue.getPendingCount();
}

export function subscribeToRetryQueue(
  listener: (queue: QueuedRequest[]) => void
): () => void {
  const queue = getRetryQueue();
  return queue.subscribe(listener);
}

export function clearRetryQueue(): void {
  const queue = getRetryQueue();
  queue.clear();
}